import React, { useEffect, useState } from 'react'
import type { InputBoxedProps } from '../InputBoxed'
import { InputBoxed } from '../InputBoxed'

export type InputCurrencyProps = Omit<InputBoxedProps, 'onChange' | 'value'> & {
  decimalSeparator?: string
  prefix?: string
  precision?: number
  value?: number | string
  onChange?: (
    event: React.FormEvent<HTMLInputElement>,
    rawValue: number,
  ) => void
}

const THOUSANDS_LENGTH = 3
const DECIMALS_LENGTH = 2

const InputCurrency = React.forwardRef<HTMLInputElement, InputCurrencyProps>(
  (
    {
      decimalSeparator = ',',
      precision = DECIMALS_LENGTH,
      prefix = 'R$',
      onChange,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState('')

    function isNumber(number: string) {
      return (
        !number.match(/[^0-9]/gi)?.length && !Number.isNaN(parseInt(number, 10))
      )
    }

    function getNumberWithDots(number: string) {
      let count = 0
      return !number || number === '0'
        ? '0'
        : number
            .split('')
            .reverse()
            .reduce((acc, value) => {
              if (count === THOUSANDS_LENGTH) {
                count = 1
                return `${value}.${acc}`
              }

              count++
              return `${value}${acc}`
            }, '')
    }

    function getRawNumber(n: string) {
      if (!n || !isNumber(n)) return 0

      const numeric = n.replace(/^0+/, '') || '0'

      if (numeric.length <= precision) {
        const padded = numeric.padStart(precision, '0')
        return parseFloat(`0.${padded}`)
      }

      const left = numeric.slice(0, numeric.length - precision)
      const right = numeric.slice(numeric.length - precision)
      return parseFloat(`${left}.${right}`)
    }

    function getFormattedNumber(number: string) {
      if (!isNumber(number)) return ''

      const isOnlyDecimals = number.length - precision <= 0
      const decimals = isOnlyDecimals
        ? number
        : number.slice(number.length - precision, number.length)
      const leftNumber = isOnlyDecimals
        ? '0'
        : number.slice(0, number.length - precision).replace(/^0+/g, '')

      const numberWithDots = getNumberWithDots(leftNumber)
      const separator = precision > 0 ? decimalSeparator : ''
      const formattedValue = `${numberWithDots}${separator}${decimals}`

      return `${prefix} ${formattedValue}`
    }

    function onChangeInput(e: React.FormEvent<HTMLInputElement>) {
      const number = e.currentTarget.value
        .replace(/\.|,| /g, '')
        .replace(prefix, '') as string

      const formattedNumber = getFormattedNumber(number)
      const rawNumber = getRawNumber(number)

      setInputValue(formattedNumber)
      if (onChange) onChange(e, rawNumber)
    }

    function updateInputValue() {
      const value = props.value

      if (typeof value !== 'number') return

      const fixedValue = value.toFixed(precision)
      const numericString = fixedValue.replace('.', '')

      setInputValue(getFormattedNumber(numericString))
    }

    useEffect(() => {
      updateInputValue()
    }, [props.value])

    return (
      <InputBoxed
        {...props}
        onChange={e => onChangeInput(e)}
        value={inputValue}
        ref={ref}
      />
    )
  },
)

export { InputCurrency }
